// First-login onboarding tour.  Walks new attorneys through the most
// important sections of the app so they know where to start.  Dismissible
// forever with "Don't show again".  Also reachable via a "Restart tour"
// link on the Profile page.
//
// Storage: localStorage['lextrack_onboarding'] = 'dismissed' means don't show.
// Missing key = show on the next main-app page load (Layout mounts the
// tour).  Set to 'completed' when the user reaches the end of the tour.
//
// Renders as an overlay with a step counter + Next / Back / Skip controls.
// Does NOT position over specific DOM elements (no highlight-cutout — that
// would need positional math + a portal); instead each step is a modal-like
// card explaining a section, and the user navigates to it themselves.

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

const STORAGE_KEY = 'lextrack_onboarding'

// Each step describes the UI as it is today; when a control named here is
// renamed or moved, update the step (lib/onboardingTour.test.js pins the
// names that have drifted before: the old "+ Add Time" on Time Entries, a
// fixed bottom-right timer button, "Tasks").
export const STEPS = [
  {
    icon: '👋',
    title: 'Welcome to Omnia Practice Management',
    body: (
      <>
        <p>Omnia Practice Management is your firm's complete case management system.  A quick 60-second tour so you know where to start.</p>
        <p className="mt-2">You can skip this at any time.  Every page has a small <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full text-xs">?</span> button in its header that opens the relevant help section — use it whenever you want more detail on a feature.</p>
      </>
    ),
  },
  {
    icon: '🧭',
    title: 'Getting around — sidebar, quick bar, search',
    body: (
      <>
        <p>The sidebar opens with <b>DAILY WORK</b>: the pages most people use all day, such as Calendar, Matters, Time Entries and To-Do.  Click any group heading to fold it away or open it again; <b>FIRM SETUP</b>, the set-up pages used a few times a year, starts folded.</p>
        <p className="mt-2">The <b>quick bar</b> at the top right of every page holds one-click shortcuts — Calendar, Matters, Time Entries, To-Do and more — and To-Do shows how many of your to-dos are due soon.  Click <b>⚙️</b> at the end of the bar to choose your own pages.  Press <b>Ctrl+K</b> (<b>⌘K</b> on a Mac) to search or jump to any page.</p>
      </>
    ),
  },
  {
    icon: '🏠',
    title: 'Dashboard — your daily starting point',
    body: (
      <>
        <p>The Dashboard shows this month's revenue, WIP hours, outstanding A/R, and trust balances at a glance (each card appears if you have access to it).</p>
        <p className="mt-2">The <b>Alerts</b> row warns you about approaching SOL deadlines, overdue invoices, inactive matters, and overdue to-dos.  Check it daily.</p>
      </>
    ),
    cta: { label: 'Go to Dashboard', to: '/dashboard' },
  },
  {
    icon: '📁',
    title: 'Matters — the heart of the app',
    body: (
      <>
        <p>Every case, transaction, or client engagement is a <b>matter</b>.  Time entries, invoices, notes, communications, trust deposits — everything flows through a matter.</p>
        <p className="mt-2">Add a client first, then create a matter linked to that client.  From there you can log time, generate invoices, and track SOL deadlines.</p>
      </>
    ),
    cta: { label: 'Go to Matters', to: '/matters' },
  },
  {
    icon: '⏱',
    title: 'Time Entries — log every billable minute',
    body: (
      <>
        <p>Start a timer with the round <b>⏱</b> button (bottom-right of the screen unless you have dragged it somewhere else), with <b>Setup &amp; Start</b> on the Time Entries page, or with <b>+ Add Time</b> on a matter.  To log time after the fact, click <b>+ Manual Entry</b> on the Time Entries page.</p>
        <p className="mt-2">In the desktop app (version 1.0.13 or later) a small floating <b>timer bar</b> takes the round ⏱ button's place while the bar is showing; hide the bar and the button comes back.</p>
        <p className="mt-2">Entries start as <b>unposted</b> (draft — editable, resumable in the timer, deletable), then move to <b>posted</b> (ready to bill — still editable, or un-post it), then <b>billed</b> (on an invoice — locked).</p>
      </>
    ),
    cta: { label: 'Go to Time Entries', to: '/time-entries' },
  },
  {
    icon: '💵',
    title: 'Billing & Invoicing',
    body: (
      <>
        <p>The <b>Billing</b> page shows WIP fees, unbilled costs, AR, and trust balances per matter — everything a bookkeeper reviews at end-of-month.</p>
        <p className="mt-2">Generate invoices from a matter's Billing tab.  Posted time entries + costs are combined into a PDF you can send to the client.</p>
      </>
    ),
    cta: { label: 'Go to Billing', to: '/billing' },
  },
  {
    icon: '🏛',
    title: 'Court Rules — California courts + per-judge chamber rules',
    body: (
      <>
        <p>The <b>Court Rules</b> page covers most of California's superior courts, the Courts of Appeal and the Supreme Court, plus the federal district, bankruptcy and Ninth Circuit courts for California — local rules, filing deadlines, and per-judge chamber rules (page limits, ex-parte procedure, courtesy copies).</p>
        <p className="mt-2">The <b>🔎 Search Rules</b> tab lets you search across every archived rule PDF — try "meet and confer" or "interlocutory appeal" and get snippets with citations.</p>
      </>
    ),
    cta: { label: 'Open Court Rules', to: '/court-rules' },
  },
  {
    icon: '🎯',
    title: "You're all set",
    body: (
      <>
        <p>That's the core loop: <b>Clients → Matters → Time Entries → Invoices → Payments</b>.</p>
        <p className="mt-2">The rest of the app supports that flow — Calendar and To-Do for deadlines and what's due, contacts for opposing counsel, trust for retainers, court rules for jurisdictional lookup, reports for analytics, Case Analysis for litigation prep (CaseMap-style facts/issues/witnesses).</p>
        <p className="mt-2 text-primary-700">Explore the sidebar.  The <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full text-xs">?</span> icon on every page opens contextual help.  Good luck!</p>
      </>
    ),
  },
]

export default function OnboardingTour() {
  const [visible, setVisible] = useState(false)
  const [step, setStep] = useState(0)
  const [dontShow, setDontShow] = useState(false)
  const navigate = useNavigate()

  // Show once on first mount (Layout, so any main-app page) unless dismissed.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved === 'dismissed' || saved === 'completed') return
      // Delay slightly so the page has painted first — avoids the tour
      // covering the Dashboard skeleton mid-load.
      const t = setTimeout(() => setVisible(true), 700)
      return () => clearTimeout(t)
    } catch { /* ignore localStorage failures */ }
  }, [])

  // Also expose a global event so the Profile page's "Restart tour" link
  // can re-launch it without needing prop drilling.  Reset dontShow so a
  // user who previously checked "Don't show again" doesn't have their
  // relaunch silently persist that decision again.
  useEffect(() => {
    const handler = () => { setStep(0); setDontShow(false); setVisible(true) }
    window.addEventListener('start-onboarding-tour', handler)
    return () => window.removeEventListener('start-onboarding-tour', handler)
  }, [])

  const dismiss = (state) => {
    // 'skipped' = user hit Skip tour (top-right) — dismiss forever, they
    //   clearly don't want it.
    // 'completed' = user finished the tour normally.
    // dontShow = user checked "Don't show again" on the last step.
    // All three write to storage so the tour doesn't nag on next refresh.
    try {
      if (state === 'skipped' || dontShow) {
        localStorage.setItem(STORAGE_KEY, 'dismissed')
      } else if (state === 'completed') {
        localStorage.setItem(STORAGE_KEY, 'completed')
      }
    } catch { /* ignore */ }
    setVisible(false)
  }

  if (!visible) return null

  const current = STEPS[step]
  const isFirst = step === 0
  const isLast = step === STEPS.length - 1

  return (
    <div className="fixed inset-0 z-[80] bg-black/40 flex items-center justify-center p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="onboarding-title"
        className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">

        <div className="p-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Step {step + 1} of {STEPS.length}
            </div>
            <button
              onClick={() => dismiss('skipped')}
              className="text-xs text-gray-400 hover:text-gray-600">
              Skip tour
            </button>
          </div>

          <div className="text-center mb-4">
            <div className="text-5xl mb-2" aria-hidden="true">{current.icon}</div>
            <h3 id="onboarding-title" className="text-xl font-bold text-gray-800">{current.title}</h3>
          </div>

          <div className="text-sm text-gray-600 space-y-2 mb-6">
            {current.body}
          </div>

          {current.cta && (
            <div className="flex justify-center mb-6">
              <button
                onClick={() => {
                  navigate(current.cta.to)
                  // Keep tour open — user can Next after seeing the page
                }}
                className="text-sm text-primary-600 hover:text-primary-800 underline">
                {current.cta.label} →
              </button>
            </div>
          )}

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {STEPS.map((_, i) => (
              <span
                key={i}
                aria-hidden="true"
                className={`inline-block w-2 h-2 rounded-full transition-colors ${
                  i === step ? 'bg-primary-600' : i < step ? 'bg-primary-300' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            <button
              onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={isFirst}
              className="text-sm text-gray-600 hover:text-gray-800 disabled:opacity-30 disabled:cursor-not-allowed">
              ← Back
            </button>
            <div className="flex items-center gap-3">
              {isLast && (
                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dontShow}
                    onChange={e => setDontShow(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  Don't show again
                </label>
              )}
              <button
                onClick={() => {
                  if (isLast) dismiss('completed')
                  else setStep(s => Math.min(STEPS.length - 1, s + 1))
                }}
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-medium transition-colors text-sm">
                {isLast ? 'Finish' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Convenience for a Profile-page "Restart tour" button
export function triggerOnboardingTour() {
  try {
    localStorage.removeItem('lextrack_onboarding')
  } catch { /* ignore */ }
  window.dispatchEvent(new Event('start-onboarding-tour'))
}
